
import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { CLOSURES } from "@/config/closures";
import { 
  calculateDays, 
  calculateNights, 
  calculateFishingPrice,
  calculateCampingPrice,
  calculateTotalPrice
} from "@/utils/permitUtils";

export interface CartItem {
  id: string;
  type: "fishing" | "camping" | "rv-booking" | "day-use" | "general-camping" | "hunting";
  permitCategory: string;
  permitName: string;
  isAnnual: boolean;
  customerName: string;
  customerEmail: string;
  startDate: Date;
  endDate: Date;
  includesCamping: boolean;
  basePrice: number;
  extraPoles?: number | null;
  totalPrice: number;
  campingTotal?: number;
  permitPrice?: number;
  campingPrice?: number;
  campingStartDate?: Date | string | null;
  campingEndDate?: Date | string | null;
  bringingPets?: boolean;
  phoneNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  licensePlate?: string;
  additionalDetails?: string;
  stayType?: "nightly" | "weekly" | "monthly";
  rvDumpFee?: boolean;
  legalAcknowledgment?: boolean;
  boatInspectionAcknowledged?: boolean;
  siteId: string;
  siteNumber?: string;
  location?: string;
  campType?: "rv" | "tent" | "general";
  days?: number;
  guestCount?: number;
  rvType?: string;
  rvLength?: number;
  bookingId?: string;
  cascadeAnglersClient?: boolean | null;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
  getTotal: () => number;
  itemCount: number;
  validateItems: () => { valid: boolean; errors: string[] };
  updateItem: (id: string, updatedItem: Partial<CartItem>) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

/**
 * True when a cart item belongs to an area that is temporarily closed.
 * Wild Horse bookings (type "camping" at a wild-horse site) are unaffected
 * unless CLOSURES.wildHorse is turned on.
 */
export const isItemClosed = (item: CartItem): boolean => {
  if (item.type === "fishing" && CLOSURES.fishing) return true;
  if (item.type === "general-camping" && CLOSURES.generalCamping) return true;
  if (item.type === "rv-booking" && CLOSURES.rvSites) return true;
  if (item.type === "camping") {
    if (item.campType === "rv") return CLOSURES.rvSites;
    if (item.campType === "general") return CLOSURES.generalCamping;
    return CLOSURES.wildHorse;
  }
  return false;
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [itemCount, setItemCount] = useState(0);

  // Load cart from localStorage
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem("tribal-trails-cart");
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        const itemsWithDates = parsedCart.map((item: any) => ({
          ...item,
          startDate: new Date(item.startDate),
          endDate: new Date(item.endDate),
          campingStartDate: item.campingStartDate ? new Date(item.campingStartDate) : item.campingStartDate,
          campingEndDate: item.campingEndDate ? new Date(item.campingEndDate) : item.campingEndDate,
        }));

        // Drop anything that belongs to a temporarily closed area
        const allowedItems = itemsWithDates.filter((item: CartItem) => !isItemClosed(item));
        const removedCount = itemsWithDates.length - allowedItems.length;

        setItems(allowedItems);
        setItemCount(allowedItems.length);

        if (removedCount > 0) {
          toast({
            title: "Items removed from your cart",
            description: `${removedCount} item${removedCount > 1 ? "s were" : " was"} removed because those reservoirs are temporarily closed due to wildfire activity.`,
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error("Error loading cart from localStorage:", error);
    }
  }, []);

  // Save cart to localStorage when updated
  useEffect(() => {
    try {
      localStorage.setItem("tribal-trails-cart", JSON.stringify(items));
      setItemCount(items.length);
      
      // Log a summary of each item's price calculation
      if (items.length > 0) {
        console.log("[CART STATE UPDATED]:", items);
        console.log("[CART ITEM PRICES]:", items.map(item => ({
          id: item.id,
          type: item.type,
          permitName: item.permitName,
          startDate: item.startDate instanceof Date ? item.startDate.toDateString() : 'Invalid date',
          endDate: item.endDate instanceof Date ? item.endDate.toDateString() : 'Invalid date',
          includesCamping: item.includesCamping,
          totalPrice: item.totalPrice,
          campingTotal: item.campingTotal
        })));
      }
    } catch (error) {
      console.error("Error saving cart to localStorage:", error);
    }
  }, [items]);

  const addItem = (item: CartItem) => {
    // Enhanced logging for debugging
    console.log("[ADD ITEM] Received item:", {
      permitName: item.permitName,
      startDate: item.startDate instanceof Date ? item.startDate.toDateString() : 'Invalid date',
      endDate: item.endDate instanceof Date ? item.endDate.toDateString() : 'Invalid date',
      days: item.days,
      totalPrice: item.totalPrice,
      campingTotal: item.campingTotal
    });
    
    // Trust the values calculated in the form - don't recalculate
    // The form has already done the correct calculations

    // Validate required fields
    if (!item.id) {
      console.error("Cannot add item without an ID", item);
      toast({
        title: "Error adding item to cart",
        description: "Missing required item ID",
        variant: "destructive"
      });
      return;
    }

    if (!item.siteId) {
      console.error("Cannot add item without a siteId", item);
      toast({
        title: "Error adding item to cart",
        description: "Missing site ID for permit",
        variant: "destructive"
      });
      return;
    }

    // Add type-specific validation
    if (item.type === 'fishing') {
      console.log("Validating fishing permit:", {
        id: item.id,
        siteId: item.siteId,
        type: item.type,
        permitName: item.permitName
      });

      if (!item.startDate || !item.endDate) {
        console.error("Cannot add fishing permit without valid dates", item);
        toast({
          title: "Error adding fishing permit",
          description: "Missing or invalid fishing dates",
          variant: "destructive"
        });
        return;
      }
      
      // Store the days and nights for later use
      const days = calculateDays(item.startDate, item.endDate);
      const nights = item.includesCamping ? calculateNights(item.startDate, item.endDate) : 0;
      
      // Only set days if not already provided by the form
      if (!item.days) {
        item.days = days;
      }
      
      console.log("[ADD ITEM] Fishing permit validation:", {
        providedDays: item.days,
        calculatedDays: days,
        nights,
        totalPrice: item.totalPrice,
        campingTotal: item.campingTotal
      });
    } else if ((item.type === "camping" || item.type === "rv-booking" || item.type === "general-camping")) {
      // Validate dates for camping items
      if (!item.startDate || !item.endDate || 
          !(item.startDate instanceof Date) || !(item.endDate instanceof Date) ||
          isNaN(item.startDate.getTime()) || isNaN(item.endDate.getTime())) {
        console.error("Cannot add camping item with invalid dates", {
          type: item.type,
          startDate: item.startDate,
          endDate: item.endDate
        });
        toast({
          title: "Error adding item to cart",
          description: "Invalid booking dates",
          variant: "destructive"
        });
        return;
      }
    }
    
    // Ensure required fields have defaults
    const newItem = { 
      ...item, 
      id: item.id || generateId(),
      legalAcknowledgment: item.legalAcknowledgment !== undefined ? item.legalAcknowledgment : true,
    };
    
    console.log("Adding item to cart:", {
      type: newItem.type,
      id: newItem.id,
      siteId: newItem.siteId,
      permitName: newItem.permitName,
      totalPrice: newItem.totalPrice,
      days: newItem.days,
      campingTotal: newItem.campingTotal
    });
    
    setItems(prevItems => [...prevItems, newItem]);
    toast({
      title: "Added to cart",
      description: getItemDescription(item),
    });

    // Log the final state after adding
    console.log("[ADD ITEM] COMPLETED - Item added to cart with final total price:", newItem.totalPrice);
  };

  // Function to update an item in the cart
  const updateItem = (id: string, updatedItem: Partial<CartItem>) => {
    console.log("[UPDATE ITEM] Updating item:", id);
    console.log("[UPDATE ITEM] Updated properties:", updatedItem);
    
    setItems(prevItems => {
      const itemIndex = prevItems.findIndex(item => item.id === id);
      
      if (itemIndex === -1) {
        console.error(`[UPDATE ITEM] Item with id ${id} not found in cart`);
        toast({
          title: "Error updating item",
          description: "The item could not be found in your cart",
          variant: "destructive"
        });
        return prevItems;
      }
      
      const currentItem = prevItems[itemIndex];
      const mergedItem = { ...currentItem, ...updatedItem };
      
      // Recalculate price if dates, includesCamping, or price-related fields have changed
      if (
        updatedItem.startDate || 
        updatedItem.endDate || 
        updatedItem.includesCamping !== undefined
      ) {
        const days = calculateDays(
          mergedItem.startDate, 
          mergedItem.endDate
        );
        
        // Update days property
        mergedItem.days = days;
        
        // Recalculate price based on item type
        if (mergedItem.type === 'fishing') {
          // Calculate fishing portion of the price
          const fishingPrice = calculateFishingPrice(
            mergedItem.permitName,
            days,
            mergedItem.basePrice
          );
          
          // Use dedicated camping dates if available, otherwise fall back to fishing dates
          const campStart = mergedItem.campingStartDate ? new Date(mergedItem.campingStartDate as any) : mergedItem.startDate;
          const campEnd = mergedItem.campingEndDate ? new Date(mergedItem.campingEndDate as any) : mergedItem.endDate;
          const nights = calculateNights(campStart, campEnd);
          
          // If includesCamping is true but nights is 0 (same day), we still charge for 1 night
          const effectiveNights = mergedItem.includesCamping && nights === 0 ? 1 : nights;
          
          // Calculate camping portion separately if applicable
          const campingPrice = mergedItem.includesCamping ? calculateCampingPrice(effectiveNights) : 0;
          
          // Set total price and store camping portion separately
          mergedItem.totalPrice = fishingPrice + campingPrice;
          mergedItem.campingTotal = campingPrice;
          
          console.log("[UPDATE ITEM] Recalculated price:", {
            days,
            nights: effectiveNights,
            fishingPrice,
            campingPrice,
            totalPrice: mergedItem.totalPrice,
            campingStartDate: mergedItem.campingStartDate,
            campingEndDate: mergedItem.campingEndDate
          });
        } else if (mergedItem.type === 'camping' || mergedItem.type === 'rv-booking' || mergedItem.type === 'general-camping') {
          const nights = calculateNights(mergedItem.startDate, mergedItem.endDate);
          // Calculate camping prices based on stay type and duration
          if (mergedItem.stayType === "nightly") {
            mergedItem.totalPrice = nights * 15;
          } else if (mergedItem.stayType === "weekly") {
            mergedItem.totalPrice = Math.ceil(nights / 7) * 75;
          } else if (mergedItem.stayType === "monthly") {
            mergedItem.totalPrice = Math.ceil(nights / 30) * 150;
          }
        }
      }
      
      // Create a new array with the updated item
      const updatedItems = [...prevItems];
      updatedItems[itemIndex] = mergedItem;
      
      toast({
        title: "Item updated",
        description: `${mergedItem.permitName} has been updated in your cart`,
      });
      
      return updatedItems;
    });
  };

  const getItemDescription = (item: CartItem) => {
    if (item.type === "fishing") {
      return `${item.permitName} has been added to your cart`;
    } else if (item.type === "camping" || item.type === "rv-booking" || item.type === "general-camping") {
      return `Booking for ${item.permitName} has been added to your cart`;
    } else if (item.type === "hunting") {
      return `${item.permitName} has been added to your cart`;
    } else {
      return `${item.permitName} has been added to your cart`;
    }
  };

  const removeItem = (id: string) => {
    setItems(prevItems => prevItems.filter(item => item.id !== id));
    toast({
      title: "Removed from cart",
      description: "Item has been removed from your cart",
    });
  };

  const clearCart = () => {
    setItems([]);
    toast({
      title: "Cart cleared",
      description: "All items have been removed from your cart",
    });
  };

  const getTotal = () => {
    return items.reduce((total, item) => {
      // Make sure we're calculating the correct total
      if (item.type === 'fishing' && item.includesCamping && item.campingTotal) {
        console.log(`[getTotal] Item ${item.id} has camping add-on. Total: ${item.totalPrice}`);
      }
      return total + item.totalPrice;
    }, 0);
  };

  const validateItems = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    for (const item of items) {
      // Check required fields
      if (!item.id) {
        errors.push(`Item missing ID: ${item.permitName}`);
      }

      if (!item.siteId) {
        errors.push(`Missing site ID for: ${item.permitName}`);
      }

      // For camping items, validate dates
      if ((item.type === "camping" || item.type === "rv-booking" || item.type === "general-camping")) {
        if (!item.startDate || !item.endDate) {
          errors.push(`Missing booking dates for: ${item.permitName}`);
        } else if (
          !(item.startDate instanceof Date) || 
          !(item.endDate instanceof Date) ||
          isNaN(item.startDate.getTime()) || 
          isNaN(item.endDate.getTime())
        ) {
          errors.push(`Invalid booking dates for: ${item.permitName}`);
        }
      }
    }

    return { 
      valid: errors.length === 0,
      errors 
    };
  };

  const generateId = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  return (
    <CartContext.Provider value={{ 
      items, 
      addItem, 
      removeItem, 
      clearCart, 
      getTotal, 
      itemCount,
      validateItems,
      updateItem
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
